import { useAuth } from "@/context/AuthContext";
import { ChatCircleDots, X, CaretLeft } from "@phosphor-icons/react";
import useMessaging from "./useMessaging";
import ConversationList from "./ConversationList";
import ThreadView from "./ThreadView";
import MessageComposer from "./MessageComposer";

export default function MessagingWidget() {
  const { user, organization } = useAuth();
  const messaging = useMessaging({ enabled: Boolean(organization) });

  // Without a membership there is nobody to message, matching the no-organization
  // banner in Layout.
  if (!organization) return null;

  const {
    isOpen, openPanel, closePanel, unread, threads, contacts,
    activeThread, messages, selectThread, openThreadWith, closeThread,
    send, sending, error,
  } = messaging;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {isOpen && (
        <div
          data-testid="messaging-panel"
          className="mb-3 w-[360px] h-[480px] bg-white border border-slate-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
        >
          <div className="h-12 shrink-0 flex items-center gap-2 px-3 border-b border-slate-200">
            {activeThread && (
              <button
                onClick={closeThread}
                aria-label="Back to conversations"
                className="p-1 rounded hover:bg-slate-100 transition-colors duration-200"
              >
                <CaretLeft size={18} />
              </button>
            )}
            <span className="font-display font-semibold text-slate-900 text-sm truncate">
              {activeThread
                ? activeThread.other?.name || "Former member"
                : "Messages"}
            </span>
            <button
              data-testid="messaging-close"
              onClick={closePanel}
              aria-label="Close messages"
              className="ml-auto p-1 rounded hover:bg-slate-100 transition-colors duration-200"
            >
              <X size={18} />
            </button>
          </div>

          {activeThread ? (
            <>
              <ThreadView
                messages={messages}
                currentUserId={user?.id}
                onVoided={() => selectThread(activeThread)}
              />
              <MessageComposer onSend={send} sending={sending} error={error} />
            </>
          ) : (
            <ConversationList
              threads={threads}
              contacts={contacts}
              onSelect={selectThread}
              onStart={openThreadWith}
            />
          )}
        </div>
      )}

      <button
        data-testid="messaging-launcher"
        onClick={isOpen ? closePanel : openPanel}
        aria-label={unread > 0 ? `Messages, ${unread} unread` : "Messages"}
        className="relative w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-lg hover:bg-slate-800 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
      >
        <ChatCircleDots size={24} weight="duotone" />
        {unread > 0 && (
          <span
            data-testid="messaging-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-status-critical text-white text-[11px] font-semibold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    </div>
  );
}
